import { FormEvent, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import { EmailTemplatesCrudSection } from '../../components/customerSuccess/EmailTemplatesCrudSection';
import { PageCard } from '../../components/layout/page/PageCard';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { SettingsTabBar } from '../../components/settings/SettingsTabBar';
import { SettingsTabItem } from '../../components/settings/SettingsTabItem';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { FormCollapsePanel } from '../../components/ui/FormCollapsePanel';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { PasswordInput } from '../../components/ui/PasswordInput';
import { usePermissions } from '../../contexts/PermissionsContext';
import {
  CustomerSuccessAiSuggestFieldSettings,
  CustomerSuccessAiSuggestSettingsView,
  CustomerSuccessAttachmentSettings,
  CustomerSuccessGeneralSettings,
  getCustomerSuccessSettings,
  saveCustomerSuccessAiSuggest,
  saveCustomerSuccessAttachments,
  saveCustomerSuccessGeneral,
} from '../../services/customerSuccessSettingsApi';
import { listSurveyForms, SurveyForm } from '../../services/customerSuccessEngagementApi';
import { CommunicationRole, listCommunicationRoles } from '../../services/communicationRolesApi';
import { EmailTemplate, listEmailTemplates } from '../../services/emailTemplatesApi';
import { matchesSurveyType } from '../../utils/matchesSurveyType';
import { CustomerSuccessSettingsTabId } from './CustomerSuccessSettingsTabId';
import { AutoMessagesSubTabId } from './AutoMessagesSubTabId';

type FieldKey =
  | 'archiveReason'
  | 'reopenReason'
  | 'reply'
  | 'internalNote'
  | 'broadcast';

const SETTINGS_TABS: SettingsTabItem<CustomerSuccessSettingsTabId>[] = [
  { id: 'general', label: 'Geral' },
  { id: 'emails', label: 'E-mails' },
  { id: 'autoMessages', label: 'Mensagens automáticas' },
  { id: 'attachments', label: 'Anexos' },
  { id: 'ai', label: 'Inteligência artificial' },
];

const AUTO_MESSAGES_SUB_TABS: SettingsTabItem<AutoMessagesSubTabId>[] = [
  { id: 'welcome', label: 'Boas-vindas' },
  { id: 'csat', label: 'CSAT' },
  { id: 'nps', label: 'NPS' },
];

const FIELD_META: Array<{
  key: FieldKey;
  title: string;
  summary: string;
  defaultMaxTokens: number;
}> = [
  {
    key: 'archiveReason',
    title: 'Motivo de arquivamento',
    summary: 'Painel Arquivar no atendimento',
    defaultMaxTokens: 280,
  },
  {
    key: 'reopenReason',
    title: 'Motivo de reabertura',
    summary: 'Painel Reabrir no atendimento',
    defaultMaxTokens: 220,
  },
  {
    key: 'reply',
    title: 'Resposta ao cliente',
    summary: 'Composer da mesa e chamados',
    defaultMaxTokens: 450,
  },
  {
    key: 'internalNote',
    title: 'Nota interna',
    summary: 'Composer com nota interna marcada',
    defaultMaxTokens: 400,
  },
  {
    key: 'broadcast',
    title: 'Resposta em massa (problema)',
    summary: 'Broadcast vinculado a problema',
    defaultMaxTokens: 450,
  },
];

const EMPTY_FIELD: CustomerSuccessAiSuggestFieldSettings = {
  enabled: true,
  systemPrompt: '',
  userInstruction: '',
  temperature: null,
  maxOutputTokens: null,
};

function emptyAi(): CustomerSuccessAiSuggestSettingsView {
  return {
    hasApiKey: false,
    endpoint: '',
    chatModel: '',
    defaultTemperature: 0.35,
    includeCannedInAiSuggest: true,
    archiveReason: { ...EMPTY_FIELD },
    reopenReason: { ...EMPTY_FIELD },
    reply: { ...EMPTY_FIELD },
    internalNote: { ...EMPTY_FIELD },
    broadcast: { ...EMPTY_FIELD },
  };
}

function emptyGeneral(): CustomerSuccessGeneralSettings {
  return {
    publicAppBaseUrl: '',
    welcomeEnabled: true,
    welcomeEmailTemplateId: null,
    csatEnabled: true,
    csatSurveyFormId: null,
    npsEnabled: true,
    npsSurveyFormId: null,
    npsFrequency: 'Monthly',
    csatIncludeTicketContact: true,
    csatCommunicationRoleId: null,
    npsCommunicationRoleId: null,
    welcomeCommunicationRoleId: null,
  };
}

function emptyAttachments(): CustomerSuccessAttachmentSettings {
  return {
    maxAttachments: 10,
    maxAttachmentBytes: 52_428_800,
    allowedExtensions: [],
  };
}

function resolveInitialTab(raw: string | null): CustomerSuccessSettingsTabId {
  if (
    raw === 'attachments'
    || raw === 'emails'
    || raw === 'ai'
    || raw === 'general'
    || raw === 'autoMessages'
  ) {
    return raw;
  }
  return 'general';
}

function resolveAutoMessagesSubTab(raw: string | null): AutoMessagesSubTabId {
  if (raw === 'welcome' || raw === 'csat' || raw === 'nps') {
    return raw;
  }
  return 'welcome';
}

function isActiveEmailTemplateOfKind(item: EmailTemplate, kind: string): boolean {
  return item.isActive && String(item.kind ?? '').trim().toLowerCase() === kind.toLowerCase();
}

export function CustomerSuccessSettingsPage() {
  const { hasPermission } = usePermissions();
  const canWrite = hasPermission('customer-success.settings.write');
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<CustomerSuccessSettingsTabId>(() =>
    resolveInitialTab(searchParams.get('tab')),
  );
  const [autoMessagesSubTab, setAutoMessagesSubTab] = useState<AutoMessagesSubTabId>(() =>
    resolveAutoMessagesSubTab(searchParams.get('sub')),
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [general, setGeneral] = useState<CustomerSuccessGeneralSettings>(emptyGeneral);
  const [attachments, setAttachments] =
    useState<CustomerSuccessAttachmentSettings>(emptyAttachments);
  const [ai, setAi] = useState<CustomerSuccessAiSuggestSettingsView>(emptyAi);
  const [welcomeTemplates, setWelcomeTemplates] = useState<EmailTemplate[]>([]);
  const [csatForms, setCsatForms] = useState<SurveyForm[]>([]);
  const [npsForms, setNpsForms] = useState<SurveyForm[]>([]);
  const [roles, setRoles] = useState<CommunicationRole[]>([]);
  const [apiKeyDraft, setApiKeyDraft] = useState('');
  const [clearApiKey, setClearApiKey] = useState(false);
  const [openField, setOpenField] = useState<FieldKey | null>('reply');

  useEffect(() => {
    setTab(resolveInitialTab(searchParams.get('tab')));
    setAutoMessagesSubTab(resolveAutoMessagesSubTab(searchParams.get('sub')));
  }, [searchParams]);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        const settings = await getCustomerSuccessSettings();
        setGeneral(settings.general);
        setAttachments(settings.attachments);
        setAi(settings.aiSuggest);
        setApiKeyDraft('');
        setClearApiKey(false);
        try {
          setRoles(await listCommunicationRoles());
        } catch {
          setRoles([]);
        }
      } catch {
        toast.error('Falha ao carregar configurações do Sucesso do Cliente.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (tab !== 'autoMessages') {
      return;
    }

    void (async () => {
      try {
        const [templates, forms] = await Promise.all([
          listEmailTemplates('customer-success'),
          listSurveyForms(),
        ]);
        setWelcomeTemplates(templates.filter((item) => isActiveEmailTemplateOfKind(item, 'welcome')));
        setCsatForms(
          forms.filter((item) => item.isActive && matchesSurveyType(item.surveyType, 'csat')),
        );
        setNpsForms(
          forms.filter((item) => item.isActive && matchesSurveyType(item.surveyType, 'nps')),
        );
      } catch {
        toast.error('Falha ao carregar templates e formulários.');
      }
    })();
  }, [tab]);

  function selectTab(next: CustomerSuccessSettingsTabId): void {
    setTab(next);
    if (next === 'autoMessages') {
      setSearchParams({ tab: next, sub: autoMessagesSubTab }, { replace: true });
      return;
    }
    setSearchParams({ tab: next }, { replace: true });
  }

  function selectAutoMessagesSubTab(next: AutoMessagesSubTabId): void {
    setAutoMessagesSubTab(next);
    setSearchParams({ tab: 'autoMessages', sub: next }, { replace: true });
  }

  function patchField(
    key: FieldKey,
    patch: Partial<CustomerSuccessAiSuggestFieldSettings>,
  ): void {
    setAi((current) => ({
      ...current,
      [key]: { ...current[key], ...patch },
    }));
  }

  async function handleSaveGeneral(
    event: FormEvent,
    successMessage = 'Configurações gerais salvas.',
  ): Promise<void> {
    event.preventDefault();
    if (!canWrite || saving) return;

    setSaving(true);
    try {
      const saved = await saveCustomerSuccessGeneral(general);
      setGeneral(saved.general);
      setAttachments(saved.attachments);
      setAi(saved.aiSuggest);
      toast.success(successMessage);
    } catch (err) {
      const detail = (err as AxiosError<{ detail?: string }>)?.response?.data?.detail;
      toast.error(detail ?? 'Não foi possível salvar as configurações.');
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveAttachments(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite || saving) return;

    setSaving(true);
    try {
      const saved = await saveCustomerSuccessAttachments(attachments);
      setGeneral(saved.general);
      setAttachments(saved.attachments);
      setAi(saved.aiSuggest);
      toast.success('Configurações de anexos salvas.');
    } catch (err) {
      const detail = (err as AxiosError<{ detail?: string }>)?.response?.data?.detail;
      toast.error(detail ?? 'Não foi possível salvar as configurações de anexos.');
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveAi(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite || saving) return;

    setSaving(true);
    try {
      const saved = await saveCustomerSuccessAiSuggest({
        apiKey: clearApiKey ? '-' : apiKeyDraft.trim() || undefined,
        endpoint: ai.endpoint,
        chatModel: ai.chatModel,
        defaultTemperature: ai.defaultTemperature,
        includeCannedInAiSuggest: ai.includeCannedInAiSuggest,
        archiveReason: ai.archiveReason,
        reopenReason: ai.reopenReason,
        reply: ai.reply,
        internalNote: ai.internalNote,
        broadcast: ai.broadcast,
      });
      setGeneral(saved.general);
      setAttachments(saved.attachments);
      setAi(saved.aiSuggest);
      setApiKeyDraft('');
      setClearApiKey(false);
      toast.success('Configurações de IA salvas.');
    } catch (err) {
      const detail = (err as AxiosError<{ detail?: string }>)?.response?.data?.detail;
      toast.error(detail ?? 'Não foi possível salvar as configurações.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <PageShell>
      <PageHeader
        title="Configurações"
        description="Parâmetros do Sucesso do Cliente: mensagens automáticas, anexos, templates de e-mail e sugestões por IA."
      />

      <SettingsTabBar tabs={SETTINGS_TABS} activeTab={tab} onChange={selectTab} />

      {loading ? (
        <LoadingSpinner label="Carregando configurações..." />
      ) : tab === 'emails' ? (
        <EmailTemplatesCrudSection
          scope="customer-success"
          canWrite={canWrite}
          fixedModuleCodes={['customer-success.engagement']}
          createButtonLabel="Novo template"
        />
      ) : tab === 'general' ? (
        <form onSubmit={(event) => void handleSaveGeneral(event)} className="erp-form">
          <PageCard
            role="tabpanel"
            aria-label="Geral"
            title="Geral"
            subtitle="URL pública usada em links de pesquisas e e-mails."
          >
            <div className="erp-form-grid">
              <label className="erp-label erp-form-grid-full">
                URL pública do app (pesquisas e e-mails)
                <input
                  className="erp-input"
                  placeholder="https://app.suaempresa.com"
                  value={general.publicAppBaseUrl}
                  disabled={!canWrite || saving}
                  onChange={(event) =>
                    setGeneral((current) => ({
                      ...current,
                      publicAppBaseUrl: event.target.value,
                    }))
                  }
                />
              </label>
            </div>
          </PageCard>

          {canWrite ? (
            <FormActionBar>
              <button type="submit" className="erp-btn-primary" disabled={saving}>
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </FormActionBar>
          ) : null}
        </form>
      ) : tab === 'autoMessages' ? (
        <form
          onSubmit={(event) =>
            void handleSaveGeneral(event, 'Mensagens automáticas salvas.')
          }
          className="erp-form"
        >
          <div className="mb-3">
            <SettingsTabBar
              tabs={AUTO_MESSAGES_SUB_TABS}
              activeTab={autoMessagesSubTab}
              onChange={selectAutoMessagesSubTab}
            />
          </div>

          {autoMessagesSubTab === 'welcome' ? (
            <PageCard
              role="tabpanel"
              aria-label="Boas-vindas"
              title="Boas-vindas"
              subtitle="Enviado quando o contrato passa a Ativo. Links e conteúdo devem estar no template de e-mail."
            >
              <div className="erp-form-grid">
                <label className="flex items-center gap-2 text-sm erp-form-grid-full">
                  <input
                    type="checkbox"
                    checked={general.welcomeEnabled}
                    disabled={!canWrite || saving}
                    onChange={(event) =>
                      setGeneral((current) => ({
                        ...current,
                        welcomeEnabled: event.target.checked,
                      }))
                    }
                  />
                  Enviar boas-vindas quando o contrato ficar Ativo
                </label>
                <label className="erp-label md:max-w-md">
                  Template de e-mail padrão
                  <select
                    className="erp-input"
                    value={general.welcomeEmailTemplateId ?? ''}
                    disabled={!canWrite || saving || !general.welcomeEnabled}
                    onChange={(event) =>
                      setGeneral((current) => ({
                        ...current,
                        welcomeEmailTemplateId: event.target.value || null,
                      }))
                    }
                  >
                    <option value="">Selecione um template</option>
                    {welcomeTemplates.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </label>
                <p className="erp-hint erp-form-grid-full -mt-2 md:max-w-md">
                  {welcomeTemplates.length === 0
                    ? 'Nenhum template ativo do tipo Boas-vindas. Crie ou edite um na aba E-mails e selecione o tipo Boas-vindas.'
                    : 'Usado quando o contrato não indicar o próprio template. Crie ou edite templates na aba E-mails (tipo Boas-vindas).'}
                </p>
                <label className="erp-label md:max-w-md">
                  Quem recebe
                  <select
                    className="erp-input"
                    value={general.welcomeCommunicationRoleId ?? ''}
                    disabled={!canWrite || saving || !general.welcomeEnabled}
                    onChange={(event) =>
                      setGeneral((current) => ({
                        ...current,
                        welcomeCommunicationRoleId: event.target.value || null,
                      }))
                    }
                  >
                    <option value="">Conta (padrão)</option>
                    {roles.filter((role) => role.isSystem).map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                </label>
                <p className="erp-hint erp-form-grid-full -mt-2 md:max-w-md">
                  Contatos da empresa com esse papel. Se ninguém tiver o papel, o envio usa o contato
                  principal ou o mais antigo com e-mail.
                </p>
              </div>
            </PageCard>
          ) : null}

          {autoMessagesSubTab === 'csat' ? (
            <PageCard
              role="tabpanel"
              aria-label="CSAT"
              title="CSAT"
              subtitle="Interruptor global e formulário padrão. Canal e contrato podem sobrescrever. O e-mail vem do formulário."
            >
              <div className="erp-form-grid">
                <label className="flex items-center gap-2 text-sm erp-form-grid-full">
                  <input
                    type="checkbox"
                    checked={general.csatEnabled}
                    disabled={!canWrite || saving}
                    onChange={(event) =>
                      setGeneral((current) => ({
                        ...current,
                        csatEnabled: event.target.checked,
                      }))
                    }
                  />
                  Enviar CSAT (interruptor global)
                </label>
                <p className="erp-hint erp-form-grid-full -mt-2">
                  Por canal: configure formulário e momentos (Resolvido / Fechado / Arquivado) em
                  Sucesso do Cliente → Canais. Sem canal no chamado, o envio automático continua só no
                  Arquivar da mesa.
                </p>
                <label className="erp-label md:max-w-md">
                  Formulário CSAT padrão
                  <select
                    className="erp-input"
                    value={general.csatSurveyFormId ?? ''}
                    disabled={!canWrite || saving || !general.csatEnabled}
                    onChange={(event) =>
                      setGeneral((current) => ({
                        ...current,
                        csatSurveyFormId: event.target.value || null,
                      }))
                    }
                  >
                    <option value="">Resolução automática (empresa / formulário padrão)</option>
                    {csatForms.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                        {item.companies?.length
                          ? ` · ${item.companies.map((company) => company.name).join(', ')}`
                          : ' · Padrão'}
                      </option>
                    ))}
                  </select>
                </label>
                <p className="erp-hint erp-form-grid-full -mt-2 md:max-w-md">
                  {csatForms.length === 0 ? (
                    <>
                      Nenhum formulário CSAT ativo.{' '}
                      <Link to="/customer-success/survey-forms" className="underline">
                        Cadastre em Formulários
                      </Link>
                      . O template de e-mail fica no próprio formulário.
                    </>
                  ) : (
                    'Usado quando canal e contrato não indicarem formulário. O e-mail de envio é o do formulário.'
                  )}
                </p>
                <label className="erp-label md:max-w-md">
                  Quem recebe
                  <select
                    className="erp-input"
                    value={general.csatCommunicationRoleId ?? ''}
                    disabled={!canWrite || saving || !general.csatEnabled}
                    onChange={(event) =>
                      setGeneral((current) => ({
                        ...current,
                        csatCommunicationRoleId: event.target.value || null,
                      }))
                    }
                  >
                    <option value="">Conta (padrão)</option>
                    {roles.filter((role) => role.isSystem).map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex items-center gap-2 text-sm erp-form-grid-full">
                  <input
                    type="checkbox"
                    checked={general.csatIncludeTicketContact}
                    disabled={!canWrite || saving || !general.csatEnabled}
                    onChange={(event) =>
                      setGeneral((current) => ({
                        ...current,
                        csatIncludeTicketContact: event.target.checked,
                      }))
                    }
                  />
                  Incluir também o contato do chamado
                </label>
              </div>
            </PageCard>
          ) : null}

          {autoMessagesSubTab === 'nps' ? (
            <PageCard
              role="tabpanel"
              aria-label="NPS"
              title="NPS"
              subtitle="Envio recorrente por contrato. O e-mail vem do formulário."
            >
              <div className="erp-form-grid">
                <label className="flex items-center gap-2 text-sm erp-form-grid-full">
                  <input
                    type="checkbox"
                    checked={general.npsEnabled}
                    disabled={!canWrite || saving}
                    onChange={(event) =>
                      setGeneral((current) => ({
                        ...current,
                        npsEnabled: event.target.checked,
                      }))
                    }
                  />
                  Enviar NPS recorrente
                </label>
                <label className="erp-label md:max-w-md">
                  Formulário NPS padrão
                  <select
                    className="erp-input"
                    value={general.npsSurveyFormId ?? ''}
                    disabled={!canWrite || saving || !general.npsEnabled}
                    onChange={(event) =>
                      setGeneral((current) => ({
                        ...current,
                        npsSurveyFormId: event.target.value || null,
                      }))
                    }
                  >
                    <option value="">Resolução automática (empresa / formulário padrão)</option>
                    {npsForms.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                        {item.companies?.length
                          ? ` · ${item.companies.map((company) => company.name).join(', ')}`
                          : ' · Padrão'}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="erp-label md:max-w-md">
                  Frequência do NPS
                  <select
                    className="erp-input"
                    value={general.npsFrequency}
                    disabled={!canWrite || saving || !general.npsEnabled}
                    onChange={(event) =>
                      setGeneral((current) => ({
                        ...current,
                        npsFrequency:
                          event.target.value as CustomerSuccessGeneralSettings['npsFrequency'],
                      }))
                    }
                  >
                    <option value="Daily">Diária</option>
                    <option value="Weekly">Semanal</option>
                    <option value="Monthly">Mensal</option>
                    <option value="Yearly">Anual</option>
                  </select>
                </label>
                <p className="erp-hint erp-form-grid-full -mt-2 md:max-w-md">
                  {npsForms.length === 0 ? (
                    <>
                      Nenhum formulário NPS ativo.{' '}
                      <Link to="/customer-success/survey-forms" className="underline">
                        Cadastre em Formulários
                      </Link>
                      . O template de e-mail fica no próprio formulário.
                    </>
                  ) : (
                    'Usado quando canal e contrato não indicarem formulário. O e-mail de envio é o do formulário.'
                  )}
                </p>
                <label className="erp-label md:max-w-md">
                  Quem recebe
                  <select
                    className="erp-input"
                    value={general.npsCommunicationRoleId ?? ''}
                    disabled={!canWrite || saving || !general.npsEnabled}
                    onChange={(event) =>
                      setGeneral((current) => ({
                        ...current,
                        npsCommunicationRoleId: event.target.value || null,
                      }))
                    }
                  >
                    <option value="">Conta (padrão)</option>
                    {roles.filter((role) => role.isSystem).map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                </label>
                <p className="erp-hint erp-form-grid-full -mt-2 md:max-w-md">
                  O NPS não usa o contato do chamado. Vai para quem tem o papel e, se ninguém tiver,
                  para o contato principal ou o mais antigo.
                </p>
              </div>
            </PageCard>
          ) : null}

          {canWrite ? (
            <FormActionBar>
              <button type="submit" className="erp-btn-primary" disabled={saving}>
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </FormActionBar>
          ) : null}
        </form>
      ) : tab === 'attachments' ? (
        <form onSubmit={(event) => void handleSaveAttachments(event)} className="erp-form">
          <PageCard
            role="tabpanel"
            aria-label="Anexos"
            title="Anexos de mensagens e chamados"
            subtitle="Limites de arquivo. Armazenamento no MinIO."
          >
            <div className="erp-form-grid">
              <label className="erp-label">
                Máximo de anexos por mensagem
                <input
                  className="erp-input"
                  type="number"
                  min={1}
                  max={50}
                  value={attachments.maxAttachments}
                  disabled={!canWrite || saving}
                  onChange={(event) =>
                    setAttachments((current) => ({
                      ...current,
                      maxAttachments: Number(event.target.value) || 1,
                    }))
                  }
                />
              </label>
              <label className="erp-label">
                Tamanho máximo por arquivo (MB)
                <input
                  className="erp-input"
                  type="number"
                  min={1}
                  max={50}
                  step={1}
                  value={Math.round(attachments.maxAttachmentBytes / (1024 * 1024))}
                  disabled={!canWrite || saving}
                  onChange={(event) =>
                    setAttachments((current) => ({
                      ...current,
                      maxAttachmentBytes:
                        Math.max(1, Number(event.target.value) || 1) * 1024 * 1024,
                    }))
                  }
                />
              </label>
              <label className="erp-label erp-form-grid-full">
                Extensões permitidas
                <textarea
                  className="erp-input min-h-[5rem] font-mono text-sm"
                  placeholder=".pdf, .png, .jpg, .mp3, .wav"
                  value={attachments.allowedExtensions.join(', ')}
                  disabled={!canWrite || saving}
                  onChange={(event) =>
                    setAttachments((current) => ({
                      ...current,
                      allowedExtensions: event.target.value
                        .split(/[\s,;]+/)
                        .map((item) => item.trim())
                        .filter(Boolean)
                        .map((item) =>
                          item.startsWith('.') ? item.toLowerCase() : `.${item.toLowerCase()}`,
                        ),
                    }))
                  }
                />
              </label>
            </div>
            <p className="erp-hint mt-3">
              Inclui anexos e arquivos embutidos no corpo (imagens, documentos, áudios). Separe as
              extensões por vírgula.
            </p>
          </PageCard>

          {canWrite ? (
            <FormActionBar>
              <button type="submit" className="erp-btn-primary" disabled={saving}>
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </FormActionBar>
          ) : null}
        </form>
      ) : (
        <form onSubmit={(event) => void handleSaveAi(event)} className="space-y-4">
          <section className="erp-card space-y-3 p-4">
            <div>
              <h2 className="text-sm font-semibold text-secondary-900">Conexão do conversation</h2>
              <p className="mt-0.5 text-xs text-secondary-500">
                Provedor OpenAI-compatible. Se a API key das configurações estiver vazia, o sistema usa a
                configuração global do ambiente (OpenAI:ApiKey).
              </p>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <label className="block md:col-span-2">
                <span className="mb-1 block text-xs font-medium text-secondary-600">
                  API key {ai.hasApiKey ? '(já configurada)' : ''}
                </span>
                <PasswordInput
                  className="erp-input w-full"
                  value={apiKeyDraft}
                  disabled={!canWrite || clearApiKey || saving}
                  placeholder={ai.hasApiKey ? '•••••••• (deixe em branco para manter)' : 'sk-…'}
                  onChange={(event) => {
                    setApiKeyDraft(event.target.value);
                    setClearApiKey(false);
                  }}
                />
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-secondary-700 md:col-span-2">
                <input
                  type="checkbox"
                  checked={clearApiKey}
                  disabled={!canWrite || saving || !ai.hasApiKey}
                  onChange={(event) => {
                    setClearApiKey(event.target.checked);
                    if (event.target.checked) {
                      setApiKeyDraft('');
                    }
                  }}
                />
                Remover API key das configurações (voltar ao fallback do ambiente)
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-secondary-600">
                  Endpoint (conversation)
                </span>
                <input
                  className="erp-input w-full"
                  value={ai.endpoint}
                  disabled={!canWrite || saving}
                  placeholder="https://api.openai.com/v1"
                  onChange={(event) => setAi((current) => ({ ...current, endpoint: event.target.value }))}
                />
                <span className="mt-1 block text-[11px] text-secondary-400">
                  URL base do provedor, sem /chat/completions. A versão (v1, v2, etc.) faz parte
                  desta URL. Ex.: https://api.openai.com/v1
                </span>
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-secondary-600">Modelo</span>
                <input
                  className="erp-input w-full"
                  value={ai.chatModel}
                  disabled={!canWrite || saving}
                  placeholder="gpt-4o-mini"
                  onChange={(event) => setAi((current) => ({ ...current, chatModel: event.target.value }))}
                />
              </label>
              <label className="block">
                <span className="mb-1 flex items-center justify-between gap-2 text-xs font-medium text-secondary-600">
                  <span>Temperatura padrão</span>
                  <span className="tabular-nums text-secondary-800">{ai.defaultTemperature.toFixed(2)}</span>
                </span>
                <input
                  type="range"
                  min={0}
                  max={2}
                  step={0.05}
                  className="w-full accent-primary-600"
                  value={ai.defaultTemperature}
                  disabled={!canWrite || saving}
                  onChange={(event) =>
                    setAi((current) => ({
                      ...current,
                      defaultTemperature: Number(event.target.value),
                    }))
                  }
                />
                <span className="mt-0.5 flex justify-between text-[11px] text-secondary-400">
                  <span>0 · preciso</span>
                  <span>2 · criativo</span>
                </span>
              </label>
              <label className="inline-flex items-center gap-2 self-end text-sm text-secondary-700">
                <input
                  type="checkbox"
                  checked={ai.includeCannedInAiSuggest}
                  disabled={!canWrite || saving}
                  onChange={(event) =>
                    setAi((current) => ({
                      ...current,
                      includeCannedInAiSuggest: event.target.checked,
                    }))
                  }
                />
                Incluir respostas prontas no contexto das sugestões
              </label>
            </div>
          </section>

          <section className="space-y-3">
            <div>
              <h2 className="text-sm font-semibold text-secondary-900">Campos com suporte a IA</h2>
              <p className="mt-0.5 text-xs text-secondary-500">
                Cada seção controla prompt, temperatura e limite de saída. Vazio = usa o padrão do
                produto.
              </p>
            </div>

            {FIELD_META.map((meta) => {
              const field = ai[meta.key];
              const expanded = openField === meta.key;
              return (
                <FormCollapsePanel
                  key={meta.key}
                  title={meta.title}
                  summary={meta.summary}
                  expanded={expanded}
                  onToggle={() => setOpenField(expanded ? null : meta.key)}
                >
                  <label className="mb-3 inline-flex items-center gap-2 text-sm text-secondary-700">
                    <input
                      type="checkbox"
                      checked={field.enabled}
                      disabled={!canWrite || saving}
                      onChange={(event) => patchField(meta.key, { enabled: event.target.checked })}
                    />
                    Habilitado
                  </label>
                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="block">
                      <div className="mb-1 flex items-center justify-between gap-2 text-xs font-medium text-secondary-600">
                        <span>Temperatura {field.temperature == null ? '(padrão)' : ''}</span>
                        <span className="tabular-nums text-secondary-800">
                          {(field.temperature ?? ai.defaultTemperature).toFixed(2)}
                        </span>
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={2}
                        step={0.05}
                        className="w-full accent-primary-600"
                        value={field.temperature ?? ai.defaultTemperature}
                        disabled={!canWrite || saving}
                        onChange={(event) =>
                          patchField(meta.key, { temperature: Number(event.target.value) })
                        }
                      />
                      <div className="mt-0.5 flex items-center justify-between gap-2 text-[11px] text-secondary-400">
                        <span>0 · preciso</span>
                        {field.temperature != null ? (
                          <button
                            type="button"
                            className="text-primary-700 hover:underline disabled:opacity-50"
                            disabled={!canWrite || saving}
                            onClick={() => patchField(meta.key, { temperature: null })}
                          >
                            Usar padrão
                          </button>
                        ) : null}
                        <span>2 · criativo</span>
                      </div>
                    </div>
                    <label className="block">
                      <span className="mb-1 block text-xs font-medium text-secondary-600">
                        Limite de saída (tokens)
                      </span>
                      <input
                        type="number"
                        min={32}
                        max={4000}
                        className="erp-input w-full"
                        value={field.maxOutputTokens ?? ''}
                        disabled={!canWrite || saving}
                        placeholder={`Padrão: ${meta.defaultMaxTokens}`}
                        onChange={(event) => {
                          const raw = event.target.value;
                          patchField(meta.key, {
                            maxOutputTokens: raw === '' ? null : Number(raw),
                          });
                        }}
                      />
                    </label>
                    <label className="block md:col-span-2">
                      <span className="mb-1 block text-xs font-medium text-secondary-600">
                        Prompt de sistema
                      </span>
                      <textarea
                        className="erp-input min-h-[7rem] w-full font-mono text-xs"
                        value={field.systemPrompt}
                        disabled={!canWrite || saving}
                        placeholder="Deixe vazio para usar o prompt padrão do produto."
                        onChange={(event) =>
                          patchField(meta.key, { systemPrompt: event.target.value })
                        }
                      />
                    </label>
                    <label className="block md:col-span-2">
                      <span className="mb-1 block text-xs font-medium text-secondary-600">
                        Instrução do usuário
                      </span>
                      <textarea
                        className="erp-input min-h-[3.5rem] w-full text-sm"
                        value={field.userInstruction}
                        disabled={!canWrite || saving}
                        placeholder="Ex.: Com base no chamado abaixo, redija a resposta ao cliente."
                        onChange={(event) =>
                          patchField(meta.key, { userInstruction: event.target.value })
                        }
                      />
                    </label>
                  </div>
                </FormCollapsePanel>
              );
            })}
          </section>

          {canWrite ? (
            <FormActionBar>
              <button type="submit" className="erp-btn-primary" disabled={saving}>
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </FormActionBar>
          ) : null}
        </form>
      )}
    </PageShell>
  );
}
